import { apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export type SubscriptionStatus = 'active' | 'paused' | 'cancelled' | 'past_due';
export type SubscriptionFrequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface Subscription {
  id: string;
  tenantId: string;
  storeId: string;
  customerId: string;
  customer?: { id: string; firstName: string; lastName: string; email?: string | null; phone?: string | null };
  customerName?: string;
  customerPhone?: string | null;
  name: string;
  description?: string | null;
  amount: string;
  currency: string;
  frequency: SubscriptionFrequency | 'biweekly';
  status: SubscriptionStatus;
  startDate: string;
  nextBillingDate: string;
  lastBilledDate?: string | null;
  endDate?: string | null;
  totalBilled: string;
  totalPayments: number;
  failedPayments: number;
  autoCharge: boolean;
  notes?: string | null;
  cancelledAt?: string | null;
  cancelledReason?: string | null;
  createdAt: string;
  updatedAt: string;
  pendingPayments?: number;
  pendingPaymentId?: string | null;
}

export interface SubscriptionSummary {
  active: number;
  paused: number;
  pastDue: number;
  dueToday: number;
  mrr: string;
}

export interface SubscriptionListParams {
  status?: SubscriptionStatus | 'all';
  customerId?: string;
  dueToday?: boolean;
  pastDue?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateSubscriptionInput {
  customerId: string;
  name: string;
  description?: string;
  amount: number;
  frequency?: SubscriptionFrequency;
  startDate: string;
  nextBillingDate?: string;
  autoCharge?: boolean;
  notes?: string;
}

export interface UpdateSubscriptionInput {
  name?: string;
  description?: string | null;
  amount?: number;
  frequency?: SubscriptionFrequency;
  nextBillingDate?: string;
  autoCharge?: boolean;
  notes?: string | null;
}

export interface CollectSubscriptionPaymentInput {
  method: 'cash' | 'card' | 'store_credit';
  amount?: number;
  cashTendered?: number;
  notes?: string;
}

export interface CollectSubscriptionPaymentResult {
  subscription: Subscription;
  payment: {
    id: string;
    amount: string;
    method: string;
    paidDate: string;
    ticketId?: string | null;
  };
  cashSessionWarning?: string | null;
}

export interface CancelSubscriptionInput {
  reason?: string;
  endDate?: string;
}

export function listSubscriptions(params?: SubscriptionListParams) {
  return apiGet<Subscription[], PaginatedMeta & { summary?: SubscriptionSummary }>(
    '/api/v1/subscriptions',
    { params },
  );
}

export function getDueTodaySubscriptions() {
  return apiGet<Subscription[]>('/api/v1/subscriptions/due-today').then((r) => r.data);
}

export function getSubscriptionSummary() {
  return apiGet<SubscriptionSummary>('/api/v1/subscriptions/summary').then((r) => r.data);
}

export function createSubscription(body: CreateSubscriptionInput) {
  return apiPost<Subscription, CreateSubscriptionInput>('/api/v1/subscriptions', body).then(
    (r) => r.data,
  );
}

export function updateSubscription(id: string, body: UpdateSubscriptionInput) {
  return apiPatch<Subscription, UpdateSubscriptionInput>(`/api/v1/subscriptions/${id}`, body).then(
    (r) => r.data,
  );
}

export function pauseSubscription(id: string) {
  return apiPost<Subscription>(`/api/v1/subscriptions/${id}/pause`).then((r) => r.data);
}

export function resumeSubscription(id: string) {
  return apiPost<Subscription>(`/api/v1/subscriptions/${id}/resume`).then((r) => r.data);
}

export function cancelSubscription(id: string, body?: CancelSubscriptionInput) {
  return apiPost<Subscription, CancelSubscriptionInput>(
    `/api/v1/subscriptions/${id}/cancel`,
    body,
  ).then((r) => r.data);
}

export function collectSubscriptionPayment(
  subscriptionId: string,
  paymentId: string,
  body: CollectSubscriptionPaymentInput,
) {
  return apiPost<CollectSubscriptionPaymentResult, CollectSubscriptionPaymentInput>(
    `/api/v1/subscriptions/${subscriptionId}/payments/${paymentId}/collect`,
    body,
  ).then((r) => r.data);
}
